import { Container, Graphics } from 'pixi.js-legacy';
import type { Laser } from '../../entities/Laser';
import { CONFIG } from '../../config';
import { drawDashedLine } from '../utils/DashedLine';

/**
 * Laser PixiJS 어댑터 (dirty flag - phase 전환 시에만 redraw)
 */
export class LaserAdapter {
    private container: Container;
    private active: Map<Laser, {
        warning: Graphics;
        laser: Graphics;
        indicator: Graphics;
        lastPhase: string;  // dirty flag
    }> = new Map();

    constructor(container: Container) {
        this.container = container;
    }

    sync(lasers: Laser[], canvasWidth: number, canvasHeight: number): void {
        // lasers 배열을 Set으로 변환하여 빠른 조회
        const laserSet = new Set(lasers);

        // active에 있지만 lasers 배열에 없거나 죽은 레이저 제거
        for (const [laser, graphics] of this.active) {
            if (!laser.isAlive || !laserSet.has(laser)) {
                graphics.warning.destroy();
                graphics.laser.destroy();
                graphics.indicator.destroy();
                this.active.delete(laser);
            }
        }

        // 살아있는 레이저 업데이트/추가
        for (const laser of lasers) {
            if (laser.isAlive) {
                if (!this.active.has(laser)) {
                    // 새 레이저 생성
                    const warning = new Graphics();
                    const laserGfx = new Graphics();
                    const indicator = new Graphics();

                    this.container.addChild(warning);
                    this.container.addChild(laserGfx);
                    this.container.addChild(indicator);

                    this.active.set(laser, { warning, laser: laserGfx, indicator, lastPhase: '' });
                }

                this.updateLaser(laser, canvasWidth, canvasHeight);
            }
        }
    }

    private updateLaser(laser: Laser, canvasWidth: number, canvasHeight: number): void {
        const graphics = this.active.get(laser);
        if (!graphics) return;

        const { warning, laser: laserGfx, indicator, lastPhase } = graphics;
        const margin = CONFIG.PLAY_AREA.MARGIN;
        const phaseChanged = lastPhase !== laser.phase;

        if (laser.phase === 'warning') {
            const warningProgress = laser.timer / CONFIG.LASER.WARNING_DURATION;
            const alpha = 0.4 + Math.sin(warningProgress * Math.PI * 6) * 0.3;

            // phase가 변경되었을 때만 Graphics 다시 그림
            if (phaseChanged) {
                graphics.lastPhase = laser.phase;

                warning.clear();
                laserGfx.clear();
                indicator.clear();

                // 점선 그리기 (최초 1회)
                if (laser.isHorizontal) {
                    drawDashedLine(
                        warning,
                        margin, laser.position,
                        canvasWidth - margin, laser.position,
                        10, 10, 0xFFFFFF, 0.7, 2
                    );
                } else {
                    drawDashedLine(
                        warning,
                        laser.position, margin,
                        laser.position, canvasHeight - margin,
                        10, 10, 0xFFFFFF, 0.7, 2
                    );
                }

                // 경고 표시자 (최초 1회)
                this.renderWarningIndicator(indicator, laser, margin, 0.5, canvasWidth, canvasHeight);
            }

            // alpha만 업데이트 (clear 없이)
            warning.visible = true;
            warning.alpha = alpha;
            indicator.visible = true;
            indicator.alpha = alpha;
            laserGfx.visible = false;

        } else if (laser.phase === 'active') {
            const activeProgress = laser.timer / CONFIG.LASER.ACTIVE_DURATION;
            const alpha = 1 - activeProgress * 0.5;

            // phase가 변경되었을 때만 Graphics 다시 그림
            if (phaseChanged) {
                graphics.lastPhase = laser.phase;

                warning.clear();
                laserGfx.clear();
                indicator.clear();

                laserGfx.beginFill(0xFFFFFF, 1);

                if (laser.isHorizontal) {
                    laserGfx.drawRect(
                        margin,
                        laser.position - CONFIG.LASER.WIDTH / 2,
                        canvasWidth - margin * 2,
                        CONFIG.LASER.WIDTH
                    );
                } else {
                    laserGfx.drawRect(
                        laser.position - CONFIG.LASER.WIDTH / 2,
                        margin,
                        CONFIG.LASER.WIDTH,
                        canvasHeight - margin * 2
                    );
                }

                laserGfx.endFill();
            }

            // alpha만 업데이트 (clear 없이)
            warning.visible = false;
            indicator.visible = false;
            laserGfx.visible = true;
            laserGfx.alpha = alpha;

        } else {
            if (phaseChanged) {
                graphics.lastPhase = laser.phase;
            }
            warning.visible = false;
            laserGfx.visible = false;
            indicator.visible = false;
        }
    }

    private renderWarningIndicator(
        g: Graphics,
        laser: Laser,
        margin: number,
        progress: number,
        canvasWidth: number,
        canvasHeight: number
    ): void {
        const size = 7;
        const pulse = Math.sin(progress * Math.PI * 6) * 0.3 + 0.7;
        const isPortrait = canvasHeight > canvasWidth;

        g.visible = true;
        g.beginFill(0xFFFFFF, pulse * 0.8);

        if (laser.isHorizontal) {
            if (isPortrait) {
                // 상단 테두리 바깥에 삼각형 (아래 방향)
                g.drawPolygon([
                    canvasWidth / 2 - size, margin - size * 2,
                    canvasWidth / 2, margin - size,
                    canvasWidth / 2 + size, margin - size * 2
                ]);
            } else {
                // 우측 테두리 바깥에 표시
                g.drawPolygon([
                    canvasWidth - margin + size, laser.position - size,
                    canvasWidth - margin + size * 2, laser.position,
                    canvasWidth - margin + size, laser.position + size
                ]);
            }
        } else {
            if (isPortrait) {
                g.drawPolygon([
                    laser.position - size, margin - size * 2,
                    laser.position, margin - size,
                    laser.position + size, margin - size * 2
                ]);
            } else {
                g.drawPolygon([
                    canvasWidth - margin + size, laser.position - size,
                    canvasWidth - margin + size * 2, laser.position,
                    canvasWidth - margin + size, laser.position + size
                ]);
            }
        }

        g.endFill();
    }

    clear(): void {
        for (const [, graphics] of this.active) {
            graphics.warning.destroy();
            graphics.laser.destroy();
            graphics.indicator.destroy();
        }
        this.active.clear();
    }

    reset(): void {
        // clearAll()로 이미 컨테이너에서 제거되었으므로 참조만 정리
        this.active.clear();
    }

    destroy(): void {
        this.clear();
    }
}
